import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useQuery } from '@tanstack/react-query';
import {
  BoardSchema,
  LabelSchema,
  MemberCandidateSchema,
  MilestoneSchema,
  ProjectMemberSchema,
  type BoardDto,
  type ColumnCategory,
  type MilestoneDto,
  type ProjectRole,
  type ProjectWorkspaceDto,
} from '@taqon/contracts';
import { z } from 'zod';
import {
  deleteNoContent,
  errorMessage,
  getData,
  patchJson,
  postJson,
} from '../../../shared/api/client';
import { PersonName } from '../../../shared/ui/person';
import { CATEGORY_LABELS, ROLE_LABELS, useCsrf, useWorkMutation, WORK } from './api';

type ManageTab = 'members' | 'board' | 'milestones' | 'labels';

/**
 * Managing a project's work (CLR-66), for its managers and the project office: the members and
 * their roles, the selected board and its columns, milestones and labels. The API enforces the
 * rules (one manager at least, a column emptied before it is removed) and its refusal is shown.
 */
export function ManageDialog({
  workspace,
  board,
  initialTab = 'members',
  onClose,
}: {
  workspace: ProjectWorkspaceDto;
  board: BoardDto | undefined;
  initialTab?: ManageTab;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<ManageTab>(initialTab);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Manage {workspace.project.code}</DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, value: ManageTab) => {
          setTab(value);
        }}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="members" label="Members" />
        <Tab value="board" label="Board" disabled={board === undefined} />
        <Tab value="milestones" label="Milestones" />
        <Tab value="labels" label="Labels" />
      </Tabs>
      <DialogContent sx={{ minHeight: 360 }}>
        {tab === 'members' && <Members workspace={workspace} />}
        {tab === 'board' && board !== undefined && (
          <BoardSettings projectId={workspace.project.id} board={board} />
        )}
        {tab === 'milestones' && <Milestones workspace={workspace} />}
        {tab === 'labels' && <Labels workspace={workspace} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Failure({ error }: { error: Error | null }) {
  if (error === null) return null;
  return (
    <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
      {errorMessage(error)}
    </Alert>
  );
}

function RolePicker({
  value,
  onChange,
  label,
  disabled,
}: {
  value: ProjectRole;
  onChange: (role: ProjectRole) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      disabled={disabled}
      sx={{ minWidth: 140 }}
      onChange={(event) => {
        onChange(event.target.value as ProjectRole);
      }}
    >
      {(Object.keys(ROLE_LABELS) as ProjectRole[]).map((role) => (
        <MenuItem key={role} value={role}>
          {ROLE_LABELS[role]}
        </MenuItem>
      ))}
    </TextField>
  );
}

function Members({ workspace }: { workspace: ProjectWorkspaceDto }) {
  const projectId = workspace.project.id;
  const csrf = useCsrf();
  const [search, setSearch] = useState('');
  const [candidate, setCandidate] = useState<{ employeeId: string; name: string } | null>(null);
  const [role, setRole] = useState<ProjectRole>('member');
  const candidates = useQuery({
    queryKey: ['project-work', 'member-candidates', projectId, search],
    queryFn: () =>
      getData(
        `${WORK}/projects/${projectId}/member-candidates?q=${encodeURIComponent(search)}`,
        z.array(MemberCandidateSchema),
      ),
  });
  const add = useWorkMutation(
    projectId,
    () =>
      postJson(
        `${WORK}/projects/${projectId}/members`,
        { employeeId: candidate?.employeeId ?? '', role },
        ProjectMemberSchema,
        csrf,
      ),
    () => {
      setCandidate(null);
      setSearch('');
    },
  );
  const change = useWorkMutation(projectId, ({ id, next }: { id: string; next: ProjectRole }) =>
    patchJson(`${WORK}/members/${id}`, { role: next }, ProjectMemberSchema, csrf),
  );
  const remove = useWorkMutation(projectId, (id: string) =>
    deleteNoContent(`${WORK}/members/${id}`, csrf),
  );
  const failure = [add, change, remove].find((m) => m.isError)?.error ?? null;
  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      <Failure error={failure} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'center' }}>
        <Autocomplete
          sx={{ flex: 1, minWidth: 240 }}
          options={candidates.data ?? []}
          value={candidate}
          inputValue={search}
          filterOptions={(options) => options}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
          onInputChange={(_, value) => {
            setSearch(value);
          }}
          onChange={(_, value) => {
            setCandidate(value);
          }}
          loading={candidates.isPending}
          noOptionsText="No employee to add"
          renderInput={(params) => <TextField {...params} size="small" label="Add an employee" />}
        />
        <RolePicker label="Role" value={role} onChange={setRole} />
        <Button
          variant="contained"
          disabled={candidate === null || add.isPending}
          onClick={() => {
            add.mutate();
          }}
        >
          Add member
        </Button>
      </Stack>
      <Stack spacing={1}>
        {workspace.members.length === 0 && (
          <Typography color="text.secondary">No member yet.</Typography>
        )}
        {workspace.members.map((member) => (
          <Stack
            key={member.id}
            direction="row"
            spacing={1.5}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <PersonName name={member.name} employeeId={member.employeeId} />
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <RolePicker
                label={`Role of ${member.name}`}
                value={member.role}
                disabled={change.isPending}
                onChange={(next) => {
                  change.mutate({ id: member.id, next });
                }}
              />
              <IconButton
                aria-label={`Remove ${member.name}`}
                disabled={remove.isPending}
                onClick={() => {
                  remove.mutate(member.id);
                }}
              >
                <DeleteOutlinedIcon />
              </IconButton>
            </Stack>
          </Stack>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Managers manage the team, boards, milestones and labels. Members create and change tasks.
        Viewers read and comment. A project keeps at least one manager.
      </Typography>
    </Stack>
  );
}

function CategoryPicker({
  value,
  onChange,
  label,
}: {
  value: ColumnCategory;
  onChange: (category: ColumnCategory) => void;
  label: string;
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      sx={{ minWidth: 150 }}
      onChange={(event) => {
        onChange(event.target.value as ColumnCategory);
      }}
    >
      {(Object.keys(CATEGORY_LABELS) as ColumnCategory[]).map((category) => (
        <MenuItem key={category} value={category}>
          {CATEGORY_LABELS[category]}
        </MenuItem>
      ))}
    </TextField>
  );
}

function BoardSettings({ projectId, board }: { projectId: string; board: BoardDto }) {
  const csrf = useCsrf();
  const [name, setName] = useState(board.name);
  const [columnName, setColumnName] = useState('');
  const [category, setCategory] = useState<ColumnCategory>('in_progress');
  const [names, setNames] = useState<Record<string, string>>({});
  const columns = [...board.columns].sort((a, b) => a.sequence - b.sequence);
  const saveBoard = useWorkMutation(
    projectId,
    (body: { name?: string; status?: 'active' | 'archived' }) =>
      patchJson(`${WORK}/boards/${board.id}`, body, BoardSchema, csrf, board.version),
  );
  const addColumn = useWorkMutation(
    projectId,
    () =>
      postJson(
        `${WORK}/boards/${board.id}/columns`,
        { name: columnName.trim(), category },
        BoardSchema,
        csrf,
      ),
    () => {
      setColumnName('');
    },
  );
  const changeColumn = useWorkMutation(
    projectId,
    ({
      id,
      body,
    }: {
      id: string;
      body: { name?: string; category?: ColumnCategory; sequence?: number };
    }) => patchJson(`${WORK}/columns/${id}`, body, BoardSchema, csrf),
  );
  const removeColumn = useWorkMutation(projectId, (id: string) =>
    deleteNoContent(`${WORK}/columns/${id}`, csrf),
  );
  const failure =
    [saveBoard, addColumn, changeColumn, removeColumn].find((m) => m.isError)?.error ?? null;
  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      <Failure error={failure} />
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <TextField
          size="small"
          label="Board name"
          value={name}
          sx={{ flex: 1 }}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <Button
          variant="contained"
          disabled={name.trim() === '' || name.trim() === board.name || saveBoard.isPending}
          onClick={() => {
            saveBoard.mutate({ name: name.trim() });
          }}
        >
          Rename
        </Button>
        <Button
          color="warning"
          disabled={saveBoard.isPending}
          onClick={() => {
            saveBoard.mutate({ status: board.status === 'active' ? 'archived' : 'active' });
          }}
        >
          {board.status === 'active' ? 'Archive board' : 'Restore board'}
        </Button>
      </Stack>
      <Typography variant="subtitle2" component="h3">
        Columns
      </Typography>
      {columns.map((column, index) => (
        <Stack key={column.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TextField
            size="small"
            label={`Name of column ${String(index + 1)}`}
            value={names[column.id] ?? column.name}
            sx={{ flex: 1 }}
            onChange={(event) => {
              setNames((current) => ({ ...current, [column.id]: event.target.value }));
            }}
            onBlur={() => {
              const next = (names[column.id] ?? column.name).trim();
              if (next !== '' && next !== column.name) {
                changeColumn.mutate({ id: column.id, body: { name: next } });
              }
            }}
          />
          <CategoryPicker
            label={`Meaning of ${column.name}`}
            value={column.category}
            onChange={(next) => {
              changeColumn.mutate({ id: column.id, body: { category: next } });
            }}
          />
          <IconButton
            aria-label={`Move ${column.name} left`}
            disabled={index === 0}
            onClick={() => {
              changeColumn.mutate({ id: column.id, body: { sequence: index } });
            }}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label={`Move ${column.name} right`}
            disabled={index === columns.length - 1}
            onClick={() => {
              changeColumn.mutate({ id: column.id, body: { sequence: index + 2 } });
            }}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label={`Delete ${column.name}`}
            disabled={columns.length === 1}
            onClick={() => {
              removeColumn.mutate(column.id);
            }}
          >
            <DeleteOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <TextField
          size="small"
          label="New column"
          value={columnName}
          sx={{ flex: 1 }}
          onChange={(event) => {
            setColumnName(event.target.value);
          }}
        />
        <CategoryPicker label="Meaning" value={category} onChange={setCategory} />
        <Button
          disabled={columnName.trim() === '' || addColumn.isPending}
          onClick={() => {
            addColumn.mutate();
          }}
        >
          Add column
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        A column&apos;s meaning says what its tasks are: a task in a Done column is complete. A
        column can be deleted once no task is in it.
      </Typography>
    </Stack>
  );
}

function MilestoneRow({ projectId, milestone }: { projectId: string; milestone: MilestoneDto }) {
  const csrf = useCsrf();
  const [name, setName] = useState(milestone.name);
  const [dueDate, setDueDate] = useState(milestone.dueDate ?? '');
  const save = useWorkMutation(
    projectId,
    (body: { name?: string; dueDate?: string | null; status?: 'open' | 'completed' }) =>
      patchJson(
        `${WORK}/milestones/${milestone.id}`,
        body,
        MilestoneSchema,
        csrf,
        milestone.version,
      ),
  );
  const dirty = name.trim() !== milestone.name || dueDate !== (milestone.dueDate ?? '');
  return (
    <Box>
      <Failure error={save.error} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'center' }}>
        <TextField
          size="small"
          label="Milestone"
          value={name}
          sx={{ flex: 1 }}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Due"
          value={dueDate}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => {
            setDueDate(event.target.value);
          }}
        />
        <Button
          disabled={!dirty || name.trim() === '' || save.isPending}
          onClick={() => {
            save.mutate({ name: name.trim(), dueDate: dueDate === '' ? null : dueDate });
          }}
        >
          Save
        </Button>
        <Button
          color={milestone.status === 'open' ? 'success' : 'inherit'}
          disabled={save.isPending}
          onClick={() => {
            save.mutate({ status: milestone.status === 'open' ? 'completed' : 'open' });
          }}
        >
          {milestone.status === 'open' ? 'Complete' : 'Reopen'}
        </Button>
      </Stack>
    </Box>
  );
}

function Milestones({ workspace }: { workspace: ProjectWorkspaceDto }) {
  const projectId = workspace.project.id;
  const csrf = useCsrf();
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const add = useWorkMutation(
    projectId,
    () =>
      postJson(
        `${WORK}/projects/${projectId}/milestones`,
        { name: name.trim(), ...(dueDate === '' ? {} : { dueDate }) },
        MilestoneSchema,
        csrf,
      ),
    () => {
      setName('');
      setDueDate('');
    },
  );
  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      {workspace.milestones.map((milestone) => (
        <MilestoneRow
          key={`${milestone.id}:${String(milestone.version)}`}
          projectId={projectId}
          milestone={milestone}
        />
      ))}
      <Failure error={add.error} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'center' }}>
        <TextField
          size="small"
          label="New milestone"
          value={name}
          sx={{ flex: 1 }}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Due"
          value={dueDate}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => {
            setDueDate(event.target.value);
          }}
        />
        <Button
          variant="contained"
          disabled={name.trim() === '' || add.isPending}
          onClick={() => {
            add.mutate();
          }}
        >
          Add milestone
        </Button>
      </Stack>
    </Stack>
  );
}

const LABEL_COLOURS = ['#2f8f6b', '#2563eb', '#b45309', '#b91c1c', '#7c3aed', '#0f766e', '#475569'];

function Labels({ workspace }: { workspace: ProjectWorkspaceDto }) {
  const projectId = workspace.project.id;
  const csrf = useCsrf();
  const [name, setName] = useState('');
  const [colour, setColour] = useState(LABEL_COLOURS[0] ?? '#2f8f6b');
  const add = useWorkMutation(
    projectId,
    () =>
      postJson(
        `${WORK}/projects/${projectId}/labels`,
        { name: name.trim(), colour },
        LabelSchema,
        csrf,
      ),
    () => {
      setName('');
    },
  );
  const change = useWorkMutation(projectId, ({ id, next }: { id: string; next: string }) =>
    patchJson(`${WORK}/labels/${id}`, { colour: next }, LabelSchema, csrf),
  );
  const remove = useWorkMutation(projectId, (id: string) =>
    deleteNoContent(`${WORK}/labels/${id}`, csrf),
  );
  const failure = [add, change, remove].find((m) => m.isError)?.error ?? null;
  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      <Failure error={failure} />
      {workspace.labels.map((label) => (
        <Stack key={label.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Box
            sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: label.colour }}
            aria-hidden
          />
          <Typography sx={{ flex: 1 }}>{label.name}</Typography>
          <ColourPicker
            label={`Colour of ${label.name}`}
            value={label.colour}
            onChange={(next) => {
              change.mutate({ id: label.id, next });
            }}
          />
          <IconButton
            aria-label={`Delete label ${label.name}`}
            onClick={() => {
              remove.mutate(label.id);
            }}
          >
            <DeleteOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <TextField
          size="small"
          label="New label"
          value={name}
          sx={{ flex: 1 }}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <ColourPicker label="Colour" value={colour} onChange={setColour} />
        <Button
          variant="contained"
          disabled={name.trim() === '' || add.isPending}
          onClick={() => {
            add.mutate();
          }}
        >
          Add label
        </Button>
      </Stack>
    </Stack>
  );
}

function ColourPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (colour: string) => void;
}) {
  const options = LABEL_COLOURS.includes(value) ? LABEL_COLOURS : [value, ...LABEL_COLOURS];
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      sx={{ minWidth: 130 }}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="span"
              sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: option }}
            />
            {option}
          </Box>
        </MenuItem>
      ))}
    </TextField>
  );
}
